import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faVideo, faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  faInstagram,
  faTiktok,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";

/**
 * Ambient mockup of the Member Portal "Content Library" flow on a neutral
 * dark surface (no teal tint): tile grid populates, highlighted tile gets
 * teal ring, three dark-gray share pills fan out (active one pulses teal),
 * lime confirm check appears.
 */

const SURFACE = "#1F1F1F";
const TILE = "#2A2A2A";
const TILE_BORDER = "#333333";
const SHARE_BTN = "#2A2A2A";
const SHARE_BORDER = "#3A3A3A";
const TEAL = "#44C2E3";
const LIME = "#C0FF00";

const CSS = `
@keyframes hiw-content-header {
  0%, 3% { opacity: 0; transform: translateY(-3px); }
  8%, 90% { opacity: 1; transform: translateY(0); }
  95%, 100% { opacity: 0; transform: translateY(-3px); }
}

@keyframes hiw-content-tile-1 { 0%, 6% { opacity: 0; transform: scale(0.92); } 12%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-2 { 0%, 9% { opacity: 0; transform: scale(0.92); } 15%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-3 { 0%, 12% { opacity: 0; transform: scale(0.92); } 18%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-4 { 0%, 15% { opacity: 0; transform: scale(0.92); } 21%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-5 { 0%, 18% { opacity: 0; transform: scale(0.92); } 24%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-6 { 0%, 21% { opacity: 0; transform: scale(0.92); } 27%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-7 { 0%, 22% { opacity: 0; transform: scale(0.92); } 28%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-8 { 0%, 24% { opacity: 0; transform: scale(0.92); } 30%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }
@keyframes hiw-content-tile-9 { 0%, 26% { opacity: 0; transform: scale(0.92); } 32%, 90% { opacity: 1; transform: scale(1); } 95%, 100% { opacity: 0; transform: scale(0.96); } }

@keyframes hiw-content-ring {
  0%, 30% { box-shadow: 0 0 0 0 transparent; }
  36%, 100% { box-shadow: 0 0 0 2px ${TEAL}; }
}

@keyframes hiw-content-share-1 { 0%, 38% { opacity: 0; transform: translate(-4px, 4px) scale(0.8); } 44%, 100% { opacity: 1; transform: translate(0, 0) scale(1); } }
@keyframes hiw-content-share-2 { 0%, 40% { opacity: 0; transform: translate(-4px, 4px) scale(0.8); } 46%, 100% { opacity: 1; transform: translate(0, 0) scale(1); } }
@keyframes hiw-content-share-3 { 0%, 42% { opacity: 0; transform: translate(-4px, 4px) scale(0.8); } 48%, 100% { opacity: 1; transform: translate(0, 0) scale(1); } }
@keyframes hiw-content-share-flash {
  0%, 55% { background: ${SHARE_BTN}; }
  60%, 67% { background: ${TEAL}; }
  72%, 100% { background: ${SHARE_BTN}; }
}
@keyframes hiw-content-check {
  0%, 65% { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  72%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

/* Resting ambient keyframes */
@keyframes hiw-content-pill-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}
@keyframes hiw-content-shimmer {
  0%, 100% { background: ${TILE}; }
  50% { background: #333A3D; }
}

/* Camera zoom - wide for grid populate, zoom in on the highlighted tile + share fan */
@keyframes hiw-content-camera {
  0%, 30% { transform: scale(1); transform-origin: 50% 50%; }
  40% { transform: scale(1.6); transform-origin: 50% 45%; }
  85% { transform: scale(1.6); transform-origin: 50% 45%; }
  95%, 100% { transform: scale(1); transform-origin: 50% 50%; }
}

.hiw-anim { will-change: transform, opacity; }

/* Active */
.hiw-content-header { animation: hiw-content-header 8s ease-out forwards; }
.hiw-content-tile-1 { animation: hiw-content-tile-1 8s ease-out forwards; }
.hiw-content-tile-2 { animation: hiw-content-tile-2 8s ease-out forwards; }
.hiw-content-tile-3 { animation: hiw-content-tile-3 8s ease-out forwards; }
.hiw-content-tile-4 { animation: hiw-content-tile-4 8s ease-out forwards; }
.hiw-content-tile-5 { animation: hiw-content-tile-5 8s ease-out forwards; }
.hiw-content-tile-6 { animation: hiw-content-tile-6 8s ease-out forwards; }
.hiw-content-tile-7 { animation: hiw-content-tile-7 8s ease-out forwards; }
.hiw-content-tile-8 { animation: hiw-content-tile-8 8s ease-out forwards; }
.hiw-content-tile-9 { animation: hiw-content-tile-9 8s ease-out forwards; }
.hiw-content-ring { animation: hiw-content-ring 8s ease-out forwards; }
.hiw-content-share-1 { animation: hiw-content-share-flash 8s ease-out forwards, hiw-content-share-1 8s ease-out forwards; }
.hiw-content-share-2 { animation: hiw-content-share-2 8s ease-out forwards; }
.hiw-content-share-3 { animation: hiw-content-share-3 8s ease-out forwards; }
.hiw-content-check { animation: hiw-content-check 8s ease-out forwards; }
.hiw-content-camera { animation: hiw-content-camera 8s cubic-bezier(0.4, 0, 0.2, 1) forwards; will-change: transform; }

/* Resting */
.hiw-content-pill-pulse { animation: hiw-content-pill-pulse 2.5s ease-in-out infinite; }
.hiw-content-shimmer { animation: hiw-content-shimmer 3.4s ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .hiw-anim, .hiw-content-camera { animation: none !important; opacity: 1 !important; transform: none !important; }
  .hiw-content-check { transform: translate(-50%, -50%) scale(1) !important; }
}
`;

const TILES = [
  { isReel: false },
  { isReel: true },
  { isReel: false },
  { isReel: false },
  { isReel: true },
  { isReel: false },
  { isReel: true },
  { isReel: false },
  { isReel: false },
];

interface Props {
  isActive: boolean;
}

const ContentAnimation = ({ isActive }: Props) => {
  return (
    <div
      className="relative aspect-[4/3] overflow-hidden"
      style={{ background: SURFACE, contain: "paint" }}
    >
      <style>{CSS}</style>

      <div className={`absolute inset-0 p-4 ${isActive ? "hiw-anim hiw-content-camera" : ""}`}>

      {/* Header strip */}
      <div className={`flex items-center justify-between mb-3 ${isActive ? "hiw-anim hiw-content-header" : ""}`}>
        <span className="text-[14px] font-black text-white tracking-tight">Library</span>
        <span
          className={`flex items-center gap-1.5 text-[11px] font-bold rounded-full px-2.5 py-1 text-black hiw-anim ${!isActive ? "hiw-content-pill-pulse" : ""}`}
          style={{ background: TEAL }}
        >
          <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
          Delivered in 48 hrs
        </span>
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-3 gap-2">
        {TILES.map((tile, i) => {
          const isHighlight = i === 1;
          const activeTileCls = `hiw-anim hiw-content-tile-${i + 1}${isHighlight ? " hiw-content-ring" : ""}`;
          const shimmerCls = !isActive && i === 4 ? "hiw-anim hiw-content-shimmer" : "";
          return (
            <div
              key={i}
              className={`relative aspect-video rounded-lg ${isActive ? activeTileCls : shimmerCls}`}
              style={{
                background: TILE,
                border: `1px solid ${TILE_BORDER}`,
              }}
            >
              {tile.isReel && (
                <FontAwesomeIcon
                  icon={faVideo}
                  className="w-3 h-3 absolute top-1.5 right-1.5"
                  style={{ color: "rgba(255,255,255,0.45)" }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Share popover and confirm - only during active */}
      {isActive && (
        <>
          <div className="absolute" style={{ top: "60%", left: "40%" }}>
            <div className="flex gap-2 items-center">
              <button
                type="button"
                className="hiw-anim hiw-content-share-1 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: SHARE_BTN, border: `1px solid ${SHARE_BORDER}` }}
                aria-label="Share to Instagram"
              >
                <FontAwesomeIcon icon={faInstagram} className="w-4 h-4" style={{ color: "white" }} />
              </button>
              <button
                type="button"
                className="hiw-anim hiw-content-share-2 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: SHARE_BTN, border: `1px solid ${SHARE_BORDER}` }}
                aria-label="Share to TikTok"
              >
                <FontAwesomeIcon icon={faTiktok} className="w-4 h-4" style={{ color: "white" }} />
              </button>
              <button
                type="button"
                className="hiw-anim hiw-content-share-3 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: SHARE_BTN, border: `1px solid ${SHARE_BORDER}` }}
                aria-label="Share to YouTube"
              >
                <FontAwesomeIcon icon={faYoutube} className="w-4 h-4" style={{ color: "white" }} />
              </button>
            </div>
          </div>

          <div
            className="hiw-anim hiw-content-check absolute w-10 h-10 rounded-full flex items-center justify-center"
            style={{
              top: "32%",
              left: "50%",
              background: LIME,
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            }}
          >
            <FontAwesomeIcon icon={faCircleCheck} className="w-5 h-5" style={{ color: "#0E0E0E" }} />
          </div>
        </>
      )}

      </div>
    </div>
  );
};

export default ContentAnimation;
